import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react"; // ✅ Added X
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const WhoWeAreForm = ({
  data = {},
  onSave,
  onRefetch,
  apiBase = `${API_BASE_URL}/about`,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    heading: "",
    tagline: "",
    content: "",
    image_url: "",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        heading: data.heading || "",
        tagline: data.tagline || "",
        content: data.content || "",
        image_url: data.image_url || "",
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("media", file);

    axios
      .post(`${API_BASE_URL}/admin/upload-media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const uploadedUrl = res.data?.data?.files?.[0]?.url;
        if (uploadedUrl) {
          setFormData((prev) => ({ ...prev, image_url: uploadedUrl }));
        }
      })
      .catch((err) => {
        console.error("upload-media error", err);
        alert("Failed to upload image. Please try again.");
      });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Session expired. Please log in again.");
        return;
      }

      const response = await fetch(`${apiBase}/who-we-are`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to save Who We Are section.");

      alert("Who We Are section saved successfully!");
      onRefetch?.();
      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save section. Please check console for details.");
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleDeleteImage = () => {
    setFormData((prev) => ({ ...prev, image_url: "" }));
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-600">Who We Are</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
          >
            <Plus size={18} /> Edit Details
          </button>
        )}
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-gray-700 text-sm font-semibold">
            <tr>
              <th className="text-left px-6 py-3">Heading</th>
              <th className="text-left px-6 py-3">Tagline</th>
              <th className="text-left px-6 py-3">Description</th>
              <th className="text-left px-6 py-3">Preview</th>
              <th className="text-center px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t text-sm text-gray-800">
              <td className="px-6 py-4">{formData.heading || "—"}</td>
              <td className="px-6 py-4">{formData.tagline || "—"}</td>
              <td className="px-6 py-4 max-w-xs truncate">
                {formData.content || "—"}
              </td>
              <td className="px-6 py-4">
                {formData.image_url ? (
                  <div className="relative inline-block">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-20 h-14 object-cover rounded-md border"
                    />
                    {/* ❌ Cross button to remove image */}
                    <button
                      type="button"
                      onClick={handleDeleteImage}
                      className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow-sm hover:bg-red-500 hover:text-white transition"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <span className="italic text-gray-400">No image</span>
                )}
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex justify-center items-center gap-3">
                  <button
                    onClick={handleEdit}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Pencil size={18} />
                  </button>
                  {formData.image_url && (
                    <button
                      onClick={handleDeleteImage}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Edit Form */}
      {isEditing && (
        <form
          onSubmit={handleSave}
          className="border-t mt-6 pt-6 space-y-6 rounded-lg"
        >
          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Heading
              </label>
              <input
                type="text"
                name="heading"
                value={formData.heading}
                onChange={handleChange}
                placeholder="Enter heading"
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Tagline
              </label>
              <input
                type="text"
                name="tagline"
                value={formData.tagline}
                onChange={handleChange}
                placeholder="Enter tagline"
                className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Description
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter description"
              rows="5"
              className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>

          {/* Upload Image */}
         <div className="col-span-1 md:col-span-2">
  <label className="block text-gray-700 font-medium mb-2">
    Upload Image
  </label>

  {formData.image_url ? (
    <div className="relative w-full max-w-xs sm:max-w-md">
      <img
        src={formData.image_url}
        alt="Preview"
        className="w-full h-44 sm:h-52 md:h-60 object-cover rounded-lg border"
      />
      <button
        type="button"
        onClick={handleDeleteImage}
        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
      >
        <X size={16} />
      </button>
    </div>
  ) : (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-blue-500 transition cursor-pointer">
      <input
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
        id="uploadInput"
      />
      <label
        htmlFor="uploadInput"
        className="cursor-pointer flex flex-col items-center gap-2 sm:gap-3 text-gray-600"
      >
        <svg
          className="h-8 w-8 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 15a4 4 0 014-4h1a4 4 0 014 4v0m0 0v6m0-6H5a2 2 0 01-2-2v-4a2 2 0 012-2h3m7 12h6a2 2 0 002-2v-4a2 2 0 00-2-2h-3m0 0a4 4 0 00-4 4v0m0-4V3m0 0L9 7m3-4l3 4"
          />
        </svg>
        <span>Click to upload an Image</span>
        <span className="text-xs text-gray-400">
          Recommended size: 1920×1080 px (16:9), JPG/PNG, upto 400 KB
        </span>
      </label>
    </div>
  )}
</div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-all"
            >
              <Save size={18} /> Save Details
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default WhoWeAreForm;
